import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { Check, Package, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionsPage = () => {
  const { getAuthHeader } = useAuth();
  const [plans, setPlans] = useState([]);
  const [mySubscription, setMySubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        axios.get(`${API}/subscriptions/plans`),
        axios.get(`${API}/subscriptions/my-subscription`, { headers: getAuthHeader() })
      ]);
      setPlans(plansRes.data);
      setMySubscription(subRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    try {
      await axios.post(
        `${API}/subscriptions/subscribe?plan_id=${planId}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Subscription activated!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Subscription failed');
    }
  };

  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel your subscription?')) return;
    
    try {
      await axios.post(`${API}/subscriptions/cancel`, {}, { headers: getAuthHeader() });
      toast.success('Subscription cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-6 lg:px-12 py-12" data-testid="subscriptions-page">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Wellness Subscription Plans
          </h1>
          <p className="text-lg text-gray-600">Monthly Dosha-matched product delivery to your doorstep</p>
        </div>

        {/* Current Subscription */}
        {mySubscription && (
          <Card className="mb-12 bg-gradient-to-r from-primary/10 to-secondary/10 border-none">
            <CardContent className="p-8">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <Package className="h-6 w-6 text-primary" />
                    <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display' }}>
                      Your Active Subscription
                    </h2>
                  </div>
                  <p className="text-lg font-medium mb-2">{mySubscription.plan_name}</p>
                  <div className="flex items-center text-sm text-gray-600 space-x-4">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      Next delivery: {new Date(mySubscription.next_delivery).toLocaleDateString()}
                    </div>
                    <div>₹{mySubscription.price}/month</div>
                  </div>
                </div>
                <Button variant="outline" onClick={handleCancel} data-testid="cancel-subscription-btn">
                  Cancel Subscription
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Subscription Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <Card key={plan.id} className={`card-lift ${plan.id === 'premium' ? 'border-2 border-primary' : ''}`}>
              {plan.id === 'premium' && (
                <div className="bg-primary text-primary-foreground text-center py-2 text-sm font-semibold">
                  MOST POPULAR
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-2xl" style={{ fontFamily: 'Playfair Display' }}>
                  {plan.name}
                </CardTitle>
                <div className="text-4xl font-bold text-primary">
                  ₹{plan.price}
                  <span className="text-base font-normal text-gray-600">/month</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={!!mySubscription}
                  className="w-full"
                  variant={plan.id === 'premium' ? 'default' : 'outline'}
                  data-testid={`subscribe-${plan.id}-btn`}
                >
                  {mySubscription ? 'Already Subscribed' : 'Subscribe Now'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Benefits Section */}
        <section className="mt-20">
          <h2 className="text-3xl font-semibold text-center mb-12" style={{ fontFamily: 'Playfair Display' }}>
            Why Subscribe?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Personalized Selection',
                description: 'Products matched to your unique Dosha profile'
              },
              {
                title: 'Seasonal Adjustments',
                description: 'Kit contents adapt to Ritucharya (seasonal changes)'
              },
              {
                title: 'Convenient Delivery',
                description: 'Auto-delivery every month, pause anytime'
              }
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SubscriptionsPage;